import ApiClient from '../ApiClient';
import type {
    Facility,
    FacilityListParams,
    AvailabilityCheckParams,
    AvailabilityResponse,
    PricingPreviewParams,
    PricingPreviewResponse,
    BookingRequest,
    BookingResponse,
    PaginatedResponse,
    FacilityBooking,
    FacilityBookingListParams,
    FacilityProfile,
    RelatedFacility,
    MemberRating,
} from '@/types/facility-api';

import type { PayOrderRequest, PayOrderResponse } from '@/types/order-api';

/**
 * Facilities API Service
 * Handles facility booking operations
 */
class FacilitiesApi {
    private basePath = 'facilities';

    /**
     * Get paginated list of facilities with optional filters
     */
    async getFacilities(params?: FacilityListParams): Promise<PaginatedResponse<Facility>> {
        try {
            const response = await ApiClient.get<PaginatedResponse<Facility>>(
                this.basePath,
                params
            );
            return response;
        } catch (error: any) {
            throw {
                status: error.status || 500,
                message: error.message || 'Failed to fetch facilities',
            };
        }
    }

    /**
     * Get facility details by ID
     */
    async getFacilityById(id: string | number): Promise<Facility> {
        try {
            const response = await ApiClient.get<{ success: boolean; data: Facility }>(
                `${this.basePath}/${id}`
            );
            return response.data;
        } catch (error: any) {
            if (error.status === 404) {
                throw { status: 404, message: 'Facility not found' };
            }
            throw error;
        }
    }

    /**
     * Check availability for a specific date and time range
     */
    async checkAvailability(
        facilityId: string,
        params: AvailabilityCheckParams
    ): Promise<AvailabilityResponse> {
        try {
            const response = await ApiClient.get<AvailabilityResponse>(
                `${this.basePath}/${facilityId}/availability`,
                params
            );
            return response;
        } catch (error: any) {
            throw {
                status: error.status || 500,
                message: error.message || 'Failed to check availability',
            };
        }
    }

    /**
     * Get pricing preview for booking
     */
    async getPricing(
        facilityId: string,
        params: PricingPreviewParams
    ): Promise<PricingPreviewResponse> {
        try {
            const response = await ApiClient.get<PricingPreviewResponse>(
                `${this.basePath}/${facilityId}/pricing`,
                params
            );
            return response;
        } catch (error: any) {
            throw {
                status: error.status || 500,
                message: error.message || 'Failed to get pricing',
            };
        }
    }

    /**
     * Book a facility
     * @throws {ApiError} 409 - Time slot conflict
     * @throws {ApiError} 400 - Validation error
     * @throws {ApiError} 401 - Authentication required
     */
    async bookFacility(data: {
        date: string;
        start_time: string;
        end_time: string;
        facility_id: number;
        guest_name?: string;
        guest_email?: string;
        guest_phone?: string;
        payment_method?: string;
    }): Promise<BookingResponse> {
        try {
            const response = await ApiClient.post<{ success: boolean; data: BookingResponse }>(
                `${this.basePath}/book`,
                data
            );
            return response.data;
        } catch (error: any) {
            if (error.status === 409) {
                throw {
                    status: 409,
                    message: error.message || 'Time slot already booked',
                };
            }
            if (error.status === 400) {
                throw {
                    status: 400,
                    message: error.message || 'Invalid booking data',
                    errors: error.errors,
                };
            }
            if (error.status === 401) {
                throw { status: 401, message: 'Authentication required' };
            }
            throw {
                status: error.status || 500,
                message: error.message || 'Failed to book facility',
            };
        }
    }

    /**
     * Get facility bookings (history) for current user
     */
    async getFacilityBookings(params?: FacilityBookingListParams): Promise<PaginatedResponse<FacilityBooking>> {
        try {
            const response = await ApiClient.get<PaginatedResponse<FacilityBooking>>(
                'facility-bookings',
                params
            );
            return response;
        } catch (error: any) {
            console.error('getFacilityBookings error:', {
                status: error.status,
                message: error.message,
                data: error.data,
                params
            });
            throw {
                status: error.status || 500,
                message: error.message || 'Failed to fetch facility bookings',
            };
        }
    }

    /**
     * Get facility booking details by ID
     */
    async getFacilityBookingById(id: string | number): Promise<FacilityBooking> {
        try {
            const response = await ApiClient.get<{ success: boolean; data: FacilityBooking }>(
                `facility-bookings/${id}`
            );
            return response.data;
        } catch (error: any) {
            if (error.status === 404) {
                throw { status: 404, message: 'Booking not found' };
            }
            throw error;
        }
    }

    /**
     * Upload payment proof for a facility booking
     */
    async uploadPaymentProof(bookingId: string | number, file: File): Promise<{ success: boolean; message: string }> {
        try {
            const formData = new FormData();
            formData.append('payment_proof', file);
            const response = await ApiClient.post<{ success: boolean; message: string }>(
                `facility-bookings/${bookingId}/upload-payment`,
                formData
            );
            return response;
        } catch (error: any) {
            throw {
                status: error.status || 500,
                message: error.message || 'Failed to upload payment proof',
            };
        }
    }

    /**
     * Request payment instructions for a facility booking via Midtrans Core API.
     * Safe to call again on a Pending/failed/expired booking to retry or switch
     * channels — each call overwrites the booking's payment_instructions.
     */
    async payFacilityBooking(bookingId: string | number, request: PayOrderRequest): Promise<PayOrderResponse> {
        try {
            const response = await ApiClient.post<{ success: boolean; data: PayOrderResponse }>(
                `facility-bookings/${bookingId}/pay`,
                request
            );
            return response.data;
        } catch (error: any) {
            if (error.status === 404) {
                throw { status: 404, message: 'Booking not found' };
            }
            if (error.status === 400) {
                throw {
                    status: 400,
                    message: error.message || 'Booking is not payable',
                };
            }
            if (error.status === 401) {
                throw { status: 401, message: 'Please login to continue' };
            }
            throw {
                status: error.status || 500,
                message: error.message || 'Failed to start payment. Please try again.',
            };
        }
    }

    async getFacilityBookingStatus(): Promise<PaginatedResponse<Facility>> {
        try {
            const response = await ApiClient.get<PaginatedResponse<Facility>>(
                'facility-booking-status'
            );
            return response;
        } catch (error: any) {
            throw {
                status: error.status || 500,
                message: error.message || 'Failed to fetch facilities',
            };
        }
    }

    // ================================================================================
    // FACILITY PROFILE METHODS - For Public Facility Profile Page
    // ================================================================================

    /**
     * Get facility profile by slug
     * Fetches complete facility data including gallery, amenities, features, etc.
     */
    async getFacilityBySlug(slug: string): Promise<FacilityProfile> {
        try {
            const response = await ApiClient.get<{ success: boolean; data: FacilityProfile }>(
                `${this.basePath}/profile/${slug}`,
                { include: ['gallery', 'amenities', 'features', 'operating_hours', 'rules', 'statistics', 'reviews'] }
            );
            return response.data;
        } catch (error: any) {
            if (error.status === 404) {
                throw { status: 404, message: 'Facility not found' };
            }
            throw {
                status: error.status || 500,
                message: error.message || 'Failed to fetch facility profile',
            };
        }
    }

    /**
     * Get all facilities for static site generation
     * Returns minimal data (id + slug) for generating static paths
     */
    async getAllFacilitiesForStaticGeneration(): Promise<Array<{ slug: string }>> {
        try {
            const response = await ApiClient.get<{ success: boolean; data: Facility[] }>(
                this.basePath,
                { per_page: 1000, fields: 'id,slug' }
            );
            return response.data.map(f => ({ slug: f.slug || String(f.id) }));
        } catch (error: any) {
            console.error('Failed to fetch facilities for static generation:', error);
            return [];
        }
    }

    /**
     * Get facility reviews with pagination and sorting
     */
    async getFacilityReviews(params: {
        facility_slug: string;
        page?: number;
        per_page?: number;
        sort_by?: 'newest' | 'highest' | 'lowest';
    }): Promise<PaginatedResponse<MemberRating>> {
        try {
            const response = await ApiClient.get<PaginatedResponse<MemberRating>>(
                `${this.basePath}/profile/${params.facility_slug}/reviews`,
                {
                    page: params.page || 1,
                    per_page: params.per_page || 10,
                    sort_by: params.sort_by || 'newest',
                }
            );
            return response;
        } catch (error: any) {
            throw {
                status: error.status || 500,
                message: error.message || 'Failed to fetch facility reviews',
            };
        }
    }

    /**
     * Get related facilities based on category, location, and popularity
     */
    async getRelatedFacilities(slug: string, limit: number = 6): Promise<RelatedFacility[]> {
        try {
            const response = await ApiClient.get<{ success: boolean; data: RelatedFacility[] }>(
                `${this.basePath}/profile/${slug}/related`,
                { limit }
            );
            return response.data;
        } catch (error: any) {
            console.error('Failed to fetch related facilities:', error);
            return [];
        }
    }

    /**
     * Follow/unfollow a facility
     */
    async toggleFacilityFollow(slug: string): Promise<{ following: boolean; followers_count: number }> {
        try {
            const response = await ApiClient.post<{ success: boolean; data: { following: boolean; followers_count: number } }>(
                `${this.basePath}/profile/${slug}/follow`,
                {}
            );
            return response.data;
        } catch (error: any) {
            if (error.status === 401) {
                throw { status: 401, message: 'Authentication required' };
            }
            throw {
                status: error.status || 500,
                message: error.message || 'Failed to update follow status',
            };
        }
    }

    /**
     * Get facility statistics
     */
    async getFacilityStatistics(slug: string): Promise<{
        total_bookings: number;
        total_visitors: number;
        total_events: number;
        total_followers: number;
        total_reviews: number;
        average_rating: number;
    }> {
        try {
            const response = await ApiClient.get<{ success: boolean; data: any }>(
                `${this.basePath}/profile/${slug}/statistics`
            );
            return response.data;
        } catch (error: any) {
            throw {
                status: error.status || 500,
                message: error.message || 'Failed to fetch facility statistics',
            };
        }
    }

    /**
     * Check if current user is following a facility
     */
    async isFollowingFacility(slug: string): Promise<boolean> {
        try {
            const response = await ApiClient.get<{ success: boolean; data: { is_following: boolean } }>(
                `${this.basePath}/profile/${slug}/follow-status`
            );
            return response.data.is_following;
        } catch (error: any) {
            // Return false on error (not authenticated, etc.)
            return false;
        }
    }
}

export const facilitiesApi = new FacilitiesApi();
